/**
 * The shared control scale — one height, one type size for every piece of page
 * chrome: toolbar buttons, filter buttons, search boxes, group-by selects,
 * segmented controls and their icon buttons.
 *
 * It is deliberately OPT-IN (`size="control"` on Button, these classes on
 * Input/SelectTrigger) rather than a change to the component defaults, because
 * the Settings pages (Organisation Settings, User Management, Security) must
 * keep the plain shadcn defaults — h-10 / px-4 / text-sm. Shrinking the
 * defaults would drag Settings down with everything else.
 *
 * Numbers: the app scales its root font to 90% (see index.css), so rem-based
 * Tailwind sizes render at 0.9x. The default control is therefore h-10 = 36px
 * tall with text-sm = 12.6px type. This scale is that, shrunk:
 *   - text 12.6px -> 11px (`text-[11px]`, an absolute px so it lands exactly
 *     on the asked-for value rather than 10.8px, which is what text-xs gives)
 *   - height 36px -> 32.4px (`h-9`, the nearest step on the rem grid)
 *   - horizontal padding 14.4px -> 10.8px (`px-3`), still comfortable for labels
 *   - glyphs 14.4px -> 12.6px (`size-3.5`), keeping icon-to-text proportion
 * Corner radius and every variant style are untouched — only the scale changes.
 */
export const CONTROL_FIELD_CLASS = "h-9 px-3 text-[11px]";

/** Glyph sizing that belongs with {@link CONTROL_FIELD_CLASS}. */
export const CONTROL_ICON_CLASS = "[&_svg]:size-3.5";

/**
 * TERTIARY — text only, no fill, no border. The same look the `ghost` Button
 * variant gives ("+ New project", "Cancel"), spelled out in classes for the
 * controls that aren't a Button and so can't take a variant (a Radix
 * SelectTrigger, for one). Width goes back to `auto` so the label and its
 * chevron sit together instead of straddling a fixed-width invisible box.
 */
export const CONTROL_TERTIARY_CLASS =
  "w-auto border-0 bg-transparent hover:bg-accent hover:text-accent-foreground";

/**
 * ACTIVE — the treatment a toolbar control wears while it is *doing* something
 * (filters applied, a non-default sort chosen), so state is visible without
 * opening the control. A tinted fill plus a primary-tinted border on top of the
 * `outline` Button variant; the label stays foreground so it keeps its contrast.
 */
export const CONTROL_ACTIVE_CLASS = "border-primary/50 bg-primary/10 hover:bg-primary/15";
